import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAppTranslation } from "../../../../constants/hooks";
import {
  useAppDispatch,
  useAppSelector,
  useAppState,
} from "../../../../constants/hooks/global-hooks";
import { RootState } from "../../../../store";
import {
  Container,
  CustomButton,
  CustomModal,
  Form,
  InfoModal,
  Input,
} from "../../../../components";
import { modifyNewsAction } from "../../../../actions/news";
import {
  resetNews,
  setNewsDescription,
  setNewsTitle,
} from "../../../../constants/reducers/news.reducer";
import { QueryKeys } from "../../../../constants/query-keys";

type MutateNewsModalProps = {
  handleVisible: () => void;
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "1px solid transparent",
  boxShadow: 12,
  borderRadius: 12,
  p: 4,
};

export function MutateNewsModal({ handleVisible }: MutateNewsModalProps) {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { id, title, description } = useAppSelector(
    (state: RootState) => state.news
  );

  const [state, setState] = useAppState({
    infoModalVisible: false,
  });

  const { mutate, error } = useMutation({
    mutationFn: modifyNewsAction,
    onSuccess: (data) => {
      queryClient.invalidateQueries([QueryKeys.newsConstantQuery]);
      dispatch(resetNews());
      return handleVisible();
    },
    onError: () => {
      setState((prevState) => ({ ...prevState, infoModalVisible: true }));
    },
  });

  const handleSave = () => {
    mutate();
  };

  const handleTitleChange = (value: string) => {
    dispatch(setNewsTitle(value));
  };

  const handleDesciptionChange = (value: string) => {
    dispatch(setNewsDescription(value));
  };

  const handleCloseInfoModal = () => {
    setState((prevState) => ({ ...prevState, infoModalVisible: false }));
  };

  return (
    <main>
      <CustomModal handleClose={handleVisible}>
        <Container style={style}>
          <Form direction="column" spacing={2}>
            <h3 className="title-text-h3">
              {id ? t("modify") : t("language-create-modal-title")}
            </h3>
            <Input
              required={true}
              onChange={handleTitleChange}
              value={title}
              label={t("news-title")}
              helperText={
                title.length === 0 ? t("filed-is-mandatory-max-255") : ""
              }
              error={title.length === 0}
              type="text"
            />
            <Input
              required={true}
              onChange={handleDesciptionChange}
              value={description}
              label={t("news-description")}
              helperText={
                description.length === 0 ? t("filed-is-mandatory-max-800") : ""
              }
              error={description.length === 0}
              type="text"
              multiline={true}
              maxLength={800}
              maxRows={5}
            />
            <CustomButton
              onClick={handleSave}
              title={t("save")}
              type="outlined"
            />
          </Form>
        </Container>
      </CustomModal>
      {state.infoModalVisible && (
        <InfoModal
          severity="error"
          message={t("language-save-error-message")}
          handleClose={handleCloseInfoModal}
          error={error}
        />
      )}
    </main>
  );
}
